import { get_token } from "@/router";

export default {
    name: "SettingsEditUserModal",

    props: {
        is_open: {
            type: Boolean,
            default: false
        },
        user: {
            type: Object,
            default: null
        }
    },

    emits: ["close", "saved"],

    data() {
        return ({
            user_form: {
                first_name: "",
                last_name: "",
                email: "",
                phone_number: ""
            },
            is_loading_user: false,
            is_saving: false,
            form_error: ""
        });
    },

    watch: {
        async is_open(next_value) {
            if (next_value) {
                this.form_error = "";
                this.set_user_edit(this.user);
                await this.loadUserProfile();
            }
        },

        user: {
            deep: true,
            handler(next_user) {
                if (this.is_open) {
                    this.set_user_edit(next_user);
                }
            }
        }
    },

    methods: {
        set_user_edit(user, profile_data = null) {
            const first_name = user?.first_name;
            const last_name = user?.last_name;
            const email = user?.email;
            const phone_number = profile_data?.phone_number || user?.phone_number;

            this.user_form.first_name = `${first_name || ""}`.trim();
            this.user_form.last_name = `${last_name || ""}`.trim();
            this.user_form.email = `${email || ""}`.trim();
            this.user_form.phone_number = phone_number || "";
        },

        get_user_id() {
            const raw_user_id = this.user && (this.user.id || this.user.user_id);
            const user_id = Number.parseInt(raw_user_id, 10);
            return Number.isInteger(user_id) ? user_id : null;
        },

        get_error_message(response_data, fallback_message) {
            const error_data = response_data?.errors
                || response_data?.detail
                || response_data?.error
                || response_data?.message
                || response_data;

            if (!error_data) {
                return fallback_message;
            }

            if (typeof error_data === "string") {
                return error_data;
            }

            if (Array.isArray(error_data)) {
                return error_data.join(", ");
            }

            if (typeof error_data === "object") {
                return Object.entries(error_data)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`)
                    .join(" | ");
            }

            return fallback_message;
        },

        async loadUserProfile() {
            const user_id = this.get_user_id();
            const auth_token = get_token();
            const update_profile_url = process.env.VUE_APP_UPDATE_USER_URL;
            const update_user_url = process.env.VUE_APP_USER_MANAGEMENT_URL || process.env.VUE_APP_VIEW_USER_URL;

            if (!Number.isInteger(user_id) || !auth_token || !update_user_url) {
                return;
            }

            this.is_loading_user = true;

            try {
                const base_user_url = update_user_url.endsWith("/")
                    ? update_user_url
                    : `${update_user_url}/`;
                const user_url = `${base_user_url}${user_id}/`;

                const user_response = await fetch(user_url, {
                    method: "GET",
                    headers: {
                        'Authorization': `Token ${auth_token}`,
                        'Content-Type': 'application/json',
                    }
                });

                if (!user_response.ok) {
                    const response_data = await user_response.json().catch(() => ({}));
                    throw new Error(this.get_error_message(response_data, "Unable to load user details."));
                }

                const user_data = await user_response.json();
                let profile_data = null;

                if (update_profile_url) {
                    const base_profile_url = update_profile_url.endsWith("/")
                        ? update_profile_url
                        : `${update_profile_url}/`;
                    const profile_url = `${base_profile_url}${user_id}/`;

                    const profile_response = await fetch(profile_url, {
                        method: "GET",
                        headers: {
                            'Authorization': `Token ${auth_token}`,
                            'Content-Type': 'application/json',
                        }
                    });

                    if (profile_response.status === 200) {
                        profile_data = await profile_response.json();
                    }
                }

                this.set_user_edit(user_data, profile_data);
            } catch (error) {
                this.form_error = error.message || "Unable to load user details.";
            } finally {
                this.is_loading_user = false;
            }
        },

        validate_user_payload() {
            if (!this.user_form.first_name || !this.user_form.last_name || !this.user_form.email) {
                return "First name, last name, and email are required.";
            }

            return "";
        },

        async update_user() {
            const user_id = this.get_user_id();
            const auth_token = get_token();
            const update_profile_url = process.env.VUE_APP_UPDATE_USER_URL;
            const update_user_url = process.env.VUE_APP_USER_MANAGEMENT_URL || process.env.VUE_APP_VIEW_USER_URL;
            const validation_error = this.validate_user_payload();

            if (validation_error) {
                this.form_error = validation_error;
                return;
            }

            if (!Number.isInteger(user_id) || !auth_token || !update_user_url) {
                this.form_error = "Unable to identify the selected user.";
                return;
            }

            this.is_saving = true;
            this.form_error = "";

            const first_name = this.user_form.first_name;
            const last_name = this.user_form.last_name;
            const email = this.user_form.email;
            const phone_number = this.user_form.phone_number;

            const base_user_url = update_user_url.endsWith("/")
                ? update_user_url
                : `${update_user_url}/`;
            const user_update_url = `${base_user_url}${user_id}/update/`;

            try {
                const user_payload = {
                    method: "POST",
                    headers: {
                        'Authorization': `Token ${auth_token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        "first_name": first_name,
                        "last_name": last_name,
                        "email": email,
                        "username": email
                    })
                };

                const user_response = await fetch(user_update_url, user_payload);
                const user_response_data = await user_response.json().catch(() => ({}));

                if (!user_response.ok) {
                    throw new Error(this.get_error_message(user_response_data, "Unable to save user."));
                }

                let profile_response_data = null;

                if (update_profile_url) {
                    const base_profile_url = update_profile_url.endsWith("/")
                        ? update_profile_url
                        : `${update_profile_url}/`;
                    const profile_update_url = `${base_profile_url}${user_id}/update/`;

                    const profile_payload = {
                        method: "POST",
                        headers: {
                            'Authorization': `Token ${auth_token}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            "phone_number": phone_number || ""
                        })
                    };

                    const profile_response = await fetch(profile_update_url, profile_payload);
                    profile_response_data = await profile_response.json().catch(() => ({}));

                    if (!profile_response.ok) {
                        throw new Error(this.get_error_message(profile_response_data, "Unable to save user profile."));
                    }
                }

                this.$emit("saved", {
                    ...this.user,
                    ...user_response_data,
                    first_name,
                    last_name,
                    email,
                    username: email,
                    phone_number: profile_response_data?.phone_number || phone_number || ""
                });
            } catch (error) {
                this.form_error = error.message || "Unable to save user.";
            } finally {
                this.is_saving = false;
            }
        },

        submit_form() {
            this.update_user();
        }
    }
};
